import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getAPIConfig, configureInternalNavigationFromCMSUrl } from '@tcp/core/src/utils';
import mediaQuickViewAbstractor from '@tcp/core/src/services/abstractors/mediaQuickView';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import pushEventToAdobeAnalytics from '@tcp/core/src/adobeAnalytics/adobe.utils';
import {
  ShoppableContainer,
  ShoppableImage,
  HotspotButton,
  SolidCircle,
  OutlineCircle,
} from '../styles/ShoppableHotspots.style';

export const trackAdobeAnalytics = (value) => {
  pushEventToAdobeAnalytics('stylitics_shoppable_image_click', value);
};

// Mock data for testing
const mockData = {
  imageUrls: {
    english:
      'https://assets.theplace.com/image/upload/w_auto,f_auto,q_auto,dpr_1.5/ecom/assets/content/tcp/us/homepage/2025/092525/usqp/group-57973_3x.jpg',
    spanish: '',
    french: '',
  },
  imageLink: '/c/girls-clothing',
  imageMetadata: { width: 4926, height: 2040 },
  hotspots: [
    { id: '1759228597581', x: 982, y: 1544, productId: 'VM4676389_SC', label: 'Point 1' },
    { id: '1759228601938', x: 3932, y: 1070, productId: '3044591_1485', label: 'Point 2' },
    { id: '1759228603666', x: 4393, y: 1859, productId: '3044143_337O', label: 'Point 3' },
  ],
};

const getLocalizedImageUrl = (imageUrls = {}) => {
  const apiConfig = getAPIConfig();
  const locale = apiConfig?.locale || 'en_US';

  const languageMap = {
    es_US: 'spanish',
    fr_CA: 'french',
  };

  const languageKey = languageMap[locale] || 'english';
  const localizedUrl = imageUrls[languageKey];
  const englishUrl = imageUrls.english || '';

  return localizedUrl && localizedUrl.trim() !== '' ? localizedUrl : englishUrl;
};

const normalizeProductIdForInventory = (productId) => {
  if (!productId) return productId;
  return productId.replace(/-/g, '_');
};

const useInventoryData = (hotspots) => {
  const [inventoryData, setInventoryData] = useState({});
  const [inventoryLoading, setInventoryLoading] = useState(true);

  const productIdsRef = useRef('');
  const currentProductIds = useMemo(
    () =>
      hotspots
        .map((hs) => hs.productId)
        .sort()
        .join(','),
    [hotspots]
  );

  useEffect(() => {
    const fetchInventory = async () => {
      if (hotspots.length === 0) {
        setInventoryLoading(false);
        return;
      }

      if (productIdsRef.current === currentProductIds) {
        return;
      }

      productIdsRef.current = currentProductIds;

      try {
        setInventoryLoading(true);
        const productIds = hotspots.map((hs) => normalizeProductIdForInventory(hs.productId));
        const response = await mediaQuickViewAbstractor.getData({ productIds });

        if (response && response.products) {
          const mappedInventory = {};
          hotspots.forEach((hs, index) => {
            const normalizedId = productIds[index];
            const originalId = hs.productId;
            if (response.products[normalizedId]) {
              mappedInventory[originalId] = response.products[normalizedId];
            }
          });
          setInventoryData(mappedInventory);
        } else {
          setInventoryData({});
        }
      } catch (error) {
        console.error('Failed to fetch inventory data:', error);
        const fallbackInventory = hotspots.reduce((acc, hs) => {
          acc[hs.productId] = { inventoryCount: 1 };
          return acc;
        }, {});
        setInventoryData(fallbackInventory);
      } finally {
        setInventoryLoading(false);
      }
    };

    fetchInventory();
  }, [currentProductIds, hotspots]);

  return { inventoryData, inventoryLoading };
};

const calculateHotspotCoordinates = (
  hotspots,
  sourceWidth,
  sourceHeight,
  displayWidth,
  displayHeight,
  inventoryData
) => {
  if (!sourceWidth || !sourceHeight || !displayWidth || !displayHeight) {
    return [];
  }

  const hotspotsWithInventory = hotspots.filter((hs) => {
    const inventory = inventoryData[hs.productId];
    return inventory && inventory.inventoryCount > 0;
  });

  const scaleX = displayWidth / sourceWidth;
  const scaleY = displayHeight / sourceHeight;

  return hotspotsWithInventory.map((hs) => {
    const inventory = inventoryData[hs.productId];
    return {
      ...hs,
      left: hs.x * scaleX,
      top: hs.y * scaleY,
      productName: inventory?.productName || hs.label || hs.productId,
    };
  });
};

const useResizeObserver = (containerRef, callback) => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const previousSizeRef = { width: 0, height: 0 };
    let rafId = null;

    const observer = new ResizeObserver((entries) => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        const entry = entries[0];
        if (!entry) return;

        const { width, height } = entry.contentRect;

        if (
          Math.abs(previousSizeRef.width - width) > 1 ||
          Math.abs(previousSizeRef.height - height) > 1
        ) {
          previousSizeRef.width = width;
          previousSizeRef.height = height;
          callbackRef.current();
        }
      });
    });

    observer.observe(container);

    // eslint-disable-next-line consistent-return
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      observer.disconnect();
    };
  }, [containerRef]);
};

const useKeyboardNavigation = (coords) => {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const hotspotRefs = useRef([]);

  useEffect(() => {
    hotspotRefs.current = hotspotRefs.current.slice(0, coords.length);
  }, [coords.length]);

  const handleKeyDown = useCallback(
    (e) => {
      if (coords.length === 0) return;

      switch (e.key) {
        case 'Tab':
          // Let natural tab order work
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev + 1 >= coords.length ? 0 : prev + 1;
            hotspotRefs.current[next]?.focus();
            return next;
          });
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev - 1 < 0 ? coords.length - 1 : prev - 1;
            hotspotRefs.current[next]?.focus();
            return next;
          });
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          hotspotRefs.current[0]?.focus();
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(coords.length - 1);
          hotspotRefs.current[coords.length - 1]?.focus();
          break;
        default:
          break;
      }
    },
    [coords.length]
  );

  return { focusedIndex, hotspotRefs, handleKeyDown };
};

const HotspotButtons = React.memo(
  ({ coords, onHotspotClick, inventoryLoading, hotspotRefs, handleKeyDown, focusedIndex }) => {
    if (inventoryLoading || coords.length === 0) {
      return null;
    }

    return coords.map((hs, index) => (
      <HotspotButton
        key={hs.id}
        ref={(el) => {
          // eslint-disable-next-line no-param-reassign
          hotspotRefs.current[index] = el;
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onHotspotClick(hs.productId);
        }}
        onKeyDown={handleKeyDown}
        left={hs.left}
        top={hs.top}
        aria-label={`View ${hs.productName || hs.productId || hs.label}`}
        aria-describedby={`hotspot-${hs.id}-description`}
        title={hs.productName || hs.productId || hs.label || ''}
        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        tabIndex={0}
        data-focused={focusedIndex === index}
      >
        <OutlineCircle />
        <SolidCircle />
        <span
          id={`hotspot-${hs.id}-description`}
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          Product hotspot {index + 1} of {coords.length}. Press Enter to view product details.
        </span>
      </HotspotButton>
    ));
  }
);

HotspotButtons.propTypes = {
  coords: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onHotspotClick: PropTypes.func.isRequired,
  inventoryLoading: PropTypes.bool.isRequired,
  hotspotRefs: PropTypes.shape({ current: PropTypes.array }).isRequired,
  handleKeyDown: PropTypes.func.isRequired,
  focusedIndex: PropTypes.number.isRequired,
};

const ShoppableImageContent = React.memo(({ imageUrl, imageAltText, onImageLoad, imgRef }) => (
  <ShoppableImage
    ref={imgRef}
    src={imageUrl}
    alt={imageAltText}
    onLoad={onImageLoad}
    onError={(e) => console.error('Image load error:', e)}
  />
));

ShoppableImageContent.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  imageAltText: PropTypes.string.isRequired,
  onImageLoad: PropTypes.func.isRequired,
  imgRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
};

const ImageLinkWrapper = React.memo(
  ({ imageLink, isHotspotClicked, imgRef, children, uniqueKey }) => {
    const handleClick = useCallback(
      (e) => {
        if (isHotspotClicked) {
          e.preventDefault();
        }
      },
      [imageLink, isHotspotClicked, imgRef]
    );

    const to = useMemo(
      () => (imageLink ? configureInternalNavigationFromCMSUrl(imageLink) : ''),
      [imageLink]
    );
    const constructAsPath = (link, key) => {
      let path = link;
      if (key) {
        path += `?icid=${key}`;
      }
      return path;
    };

    if (!imageLink) {
      return children;
    }

    return (
      <Anchor
        to={to}
        asPath={constructAsPath(imageLink, uniqueKey)}
        onClick={handleClick}
        noLink={false}
        style={{ display: 'block', width: '100%', height: '100%' }}
        dataLocator="shoppable-hotspots-image-link"
        aria-label="View collection"
      >
        {children}
      </Anchor>
    );
  }
);

ImageLinkWrapper.propTypes = {
  imageLink: PropTypes.string,
  isHotspotClicked: PropTypes.bool.isRequired,
  imgRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  children: PropTypes.node.isRequired,
};

ImageLinkWrapper.defaultProps = {
  imageLink: null,
};

const useCoordinateUpdates = (
  containerRef,
  imgRef,
  imageLoaded,
  hotspots,
  originalWidth,
  originalHeight,
  inventoryData
) => {
  const [coords, setCoords] = useState([]);

  const hotspotsRef = useRef(hotspots);
  const inventoryDataRef = useRef(inventoryData);

  useEffect(() => {
    hotspotsRef.current = hotspots;
  }, [hotspots]);

  useEffect(() => {
    inventoryDataRef.current = inventoryData;
  }, [inventoryData]);

  const updateCoords = useCallback(() => {
    const container = containerRef.current;
    const img = imgRef.current;

    if (!container || !img || !imageLoaded) {
      return;
    }

    const displayWidth = container.clientWidth;
    const displayHeight = container.clientHeight;
    const sourceWidth = originalWidth || img.naturalWidth;
    const sourceHeight = originalHeight || img.naturalHeight;

    if (!sourceWidth || !sourceHeight || !displayWidth || !displayHeight) {
      return;
    }

    const newCoords = calculateHotspotCoordinates(
      hotspotsRef.current,
      sourceWidth,
      sourceHeight,
      displayWidth,
      displayHeight,
      inventoryDataRef.current
    );

    setCoords(newCoords);
  }, [containerRef, imgRef, originalWidth, originalHeight, imageLoaded]);

  return { coords, updateCoords };
};

export default function ShoppableHotspots({
  data = mockData,
  onQuickViewOpenClick,
  brandName = '',
  uniqueKey = '',
}) {
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHotspotClicked, setIsHotspotClicked] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const { hotspots = [], imageMetadata, imageLink, imageAltText = 'Shoppable lookbook' } = data;
  const imageUrl = useMemo(() => getLocalizedImageUrl(data.imageUrls), [data.imageUrls]);
  const { inventoryData, inventoryLoading } = useInventoryData(hotspots);

  const originalWidth = imageMetadata?.width || 0;
  const originalHeight = imageMetadata?.height || 0;

  const { coords, updateCoords } = useCoordinateUpdates(
    containerRef,
    imgRef,
    imageLoaded,
    hotspots,
    originalWidth,
    originalHeight,
    inventoryData
  );

  const { focusedIndex, hotspotRefs, handleKeyDown } = useKeyboardNavigation(coords);

  useResizeObserver(containerRef, updateCoords);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    requestAnimationFrame(() => {
      setIsInitialized(true);
    });
  }, []);

  // Handle case where image loads before onLoad handler is attached (cached images)
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setImageLoaded(true);
      requestAnimationFrame(() => {
        setIsInitialized(true);
      });
    }
  }, [imageUrl]);

  // Use ref to prevent multiple calls when updateCoords changes
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (imageLoaded && !inventoryLoading && isInitialized && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      updateCoords();
    }
  }, [imageLoaded, inventoryLoading, isInitialized, updateCoords]);

  const handleHotspotClick = useCallback(
    (productId) => {
      setIsHotspotClicked(true);

      if (uniqueKey) {
        const transformedProductId = productId.replace(/_/g, '-');

        const analyticsValue = `${uniqueKey}_${transformedProductId}`;
        trackAdobeAnalytics(analyticsValue);
      }

      if (onQuickViewOpenClick) {
        onQuickViewOpenClick({
          colorProductId: normalizeProductIdForInventory(productId),
          generalProductId: normalizeProductIdForInventory(productId),
          fromPage: 'plp',
          brand: brandName,
          eVar152: uniqueKey ? `${uniqueKey}_${productId}` : undefined,
        });
      }
      setTimeout(() => setIsHotspotClicked(false), 100);
    },
    [onQuickViewOpenClick, brandName, uniqueKey]
  );

  const hotspotCountMessage = useMemo(() => {
    const count = coords.length;
    if (count === 0) return 'No interactive hotspots available';
    if (count === 1) return '1 interactive hotspot available';
    return `${count} interactive hotspots available`;
  }, [coords.length]);

  return (
    <ShoppableContainer
      ref={containerRef}
      role="region"
      aria-label="Shoppable image with interactive product hotspots"
    >
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {isInitialized && !inventoryLoading && hotspotCountMessage}
      </div>

      <ImageLinkWrapper
        imageLink={imageLink}
        isHotspotClicked={isHotspotClicked}
        imgRef={imgRef}
        uniqueKey={uniqueKey}
      >
        <ShoppableImageContent
          imageUrl={imageUrl}
          imageAltText={imageAltText}
          onImageLoad={handleImageLoad}
          imgRef={imgRef}
        />
        {isInitialized && (
          <HotspotButtons
            coords={coords}
            onHotspotClick={handleHotspotClick}
            inventoryLoading={inventoryLoading}
            hotspotRefs={hotspotRefs}
            handleKeyDown={handleKeyDown}
            focusedIndex={focusedIndex}
          />
        )}
      </ImageLinkWrapper>
    </ShoppableContainer>
  );
}

ShoppableHotspots.propTypes = {
  data: PropTypes.shape({
    imageUrls: PropTypes.shape({
      english: PropTypes.string,
      spanish: PropTypes.string,
      french: PropTypes.string,
    }),
    imageLink: PropTypes.string,
    imageAltText: PropTypes.string,
    imageMetadata: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),
    hotspots: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        productId: PropTypes.string.isRequired,
        label: PropTypes.string,
      })
    ),
  }),
  onQuickViewOpenClick: PropTypes.func,
  brandName: PropTypes.string,
  uniqueKey: PropTypes.string,
};

ShoppableHotspots.defaultProps = {
  data: mockData,
  onQuickViewOpenClick: null,
  brandName: '',
  uniqueKey: '',
};
